import { YourAppsMenu } from "../../../components/YourAppsMenu";
import { useCentralReady } from "../../../context/CentralReadyContext";
import { cmsSettingsAccess } from "../../../helpers/cmsSettingsAccess";
import { SignOutButton } from "../../../components/common/SignOutButton";
import { BrandMark } from '../../../components/brand/BrandMark';
import { useCacheRefresh } from "./CacheRefreshProvider";
import { useEffect, useRef, useState } from "react";
import { AccountStatus, AccountType, UserType } from "../../../config/Enums";
import { TokenService } from "../../../services/TokenService";
import { SiteSwitcher } from "../../../services/SiteSwitcher";
import { AvailableSite } from "../../../models/AppModels";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRightFromBracket, faExclamationTriangle, faGlobe, faLock, faUnlockKeyhole, faUserShield, faBuilding } from "@fortawesome/free-solid-svg-icons";
import { faIdCard } from "@fortawesome/free-regular-svg-icons";
import { Alert, Dropdown, FormSelect, Navbar, Nav, NavDropdown } from "react-bootstrap";
import { AuthService } from "../../../services/AuthService";
import { EventBus } from "../../../helpers/EventBus";
import { type AppTheme, themeLabels, useTheme } from "../../../context/ThemeProvider";
import { Formatters } from "../../../helpers/Fomatters";
import { centralEnabled, cmsSession } from '../../../services/centralAuth';
import { IAccountUiUpdate } from "../../../helpers/PaymentReturnHelper";

const AdminHeader = () => {
  const location = useLocation();
  const sessionReady = useCentralReady();
  const navigate = useNavigate();
  const [accStatus, setAccStatus] = useState<AccountStatus | null>(null);
  const [accType, setAccType] = useState<AccountType | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [profileLabel, setProfileLabel] = useState("My account");
  const [selectedSite, setSelectedSite] = useState<string>("");
  const [sites, setSites] = useState<Array<AvailableSite>>([]);
  const [userHasSiteAccount, setUserHasSiteAccount] = useState<boolean>(true);

  const authService = useRef<AuthService | null>(null);
  const tokenServiceRef = useRef<TokenService | null>(null);
  const siteSwitchRef = useRef<SiteSwitcher | null>(null);
  const { theme, setTheme, availableThemes } = useTheme();
  const isSuspended = accStatus === AccountStatus.Suspended;
  const isApproachingPaymentDue = accStatus === AccountStatus.ApproachingPaymentDue;
  const isOverdue = accStatus === AccountStatus.Overdue;

  const syncAccountState = (siteId: string, availableSites: Array<AvailableSite>): void => {
    const matchedSite = availableSites.find((site) => site.SiteId === siteId) ?? availableSites[0] ?? null;
    const accountDetails = matchedSite?.AccountDetails ?? null;

    setAccStatus(accountDetails?.AccountStatus ?? AccountStatus.Normal);
    setAccType(accountDetails?.AccountType ?? AccountType.Paid);
  };

  const getAvailableSites = async () => {
    if (siteSwitchRef.current) {
      const result = await siteSwitchRef.current.getAvailableSites();
      if (result?.length > 0) {
        setSites(result);
        syncAccountState(selectedSite || result[0].SiteId, result);
      }
    }
  };

  const handleSiteSelect = (evt: React.ChangeEvent<HTMLSelectElement>) => {
    if (siteSwitchRef.current) {
      const { value } = evt.target;
      if (value) {
        siteSwitchRef.current.setSelectedSiteKey(evt.target.value);
      }
    }
  };

  const handleSignOut = () => {
    if (authService.current) {
      authService.current.SignOut();
    }
  };

  const handleSiteSwitch = (siteid: string): void => {
    if (siteid) {
      setSelectedSite(siteid);
      syncAccountState(siteid, sites);
      navigate("/content", { replace: true });
    }
  };

  useEffect(() => {
    const handleNewSiteAdded = (site: AvailableSite) => {
      if (site) {
        setSites(prev => {
          const nextSites = [...prev, site];
          if (!selectedSite) {
            syncAccountState(site.SiteId, nextSites);
          }
          return nextSites;
        });
      }
    }

    const handleSiteRemoved = (siteid: string) => {
      if (siteid) {
        setSites(prev => {
          const nextSites = prev.filter(site => site.SiteId !== siteid);
          if (selectedSite === siteid) {
            syncAccountState(nextSites[0]?.SiteId ?? "", nextSites);
          }
          return nextSites;
        });
      }
    }

    const handleAccountUpdated = async (update?: IAccountUiUpdate) => {
      if (update?.siteId && update.accountDetails) {
        setSites(prev => {
          const nextSites = prev.map(site =>
            site.SiteId === update.siteId
              ? {
                  ...site,
                  AccountDetails: {
                    ...(site.AccountDetails ?? {}),
                    ...update.accountDetails
                  }
                }
              : site
          );

          syncAccountState(selectedSite || update.siteId, nextSites);
          return nextSites;
        });

        return;
      }

      await getAvailableSites();
    };

    // Subscribe to EventBus events
    EventBus.getInstance().subscribe(EventBus.Event.OnNewSiteAdded, handleNewSiteAdded);
    EventBus.getInstance().subscribe(EventBus.Event.OnSiteRemoved, handleSiteRemoved);
    EventBus.getInstance().subscribe(EventBus.Event.OnSiteSelected, handleSiteSwitch);
    EventBus.getInstance().subscribe(EventBus.Event.OnAccountUpdated, handleAccountUpdated);

    // Cleanup to prevent duplicate subscriptions
    return () => {
      EventBus.getInstance().unsubscribe(EventBus.Event.OnNewSiteAdded, handleNewSiteAdded);
      EventBus.getInstance().unsubscribe(EventBus.Event.OnSiteRemoved, handleSiteRemoved);
      EventBus.getInstance().unsubscribe(EventBus.Event.OnSiteSelected, handleSiteSwitch);
      EventBus.getInstance().unsubscribe(EventBus.Event.OnAccountUpdated, handleAccountUpdated);
    };

  }, [selectedSite]);

  useEffect(() => {
    if (!tokenServiceRef.current) {
      tokenServiceRef.current = new TokenService();
    }
    setProfileLabel((centralEnabled ? cmsSession()?.context.displayName?.trim() : null) || tokenServiceRef.current.getTokenNoRedirect()?.email || "My account");

    if (!authService.current) {
      authService.current = new AuthService();
    }

    if (!siteSwitchRef.current) {
      siteSwitchRef.current = new SiteSwitcher();
      const sitekey = siteSwitchRef.current.getSelectedSiteKey();
      if (sitekey) {
        setSelectedSite(sitekey);
      }
    }

    const getData = async () => {
      try {
        await getAvailableSites();
        setIsAdmin(tokenServiceRef.current?.isSystemAdmin() ?? false);
      } catch (error) {
        console.log(error);
      }
    }

    if (sessionReady) void getData();

  }, [sessionReady]);

  useEffect(() => {
    if (sites.length > 0) {
      syncAccountState(selectedSite, sites);
    }
  }, [selectedSite, sites]);

  const cacheRefresh = useCacheRefresh();

  const selectedSiteSummary = sites.find((site) => site.SiteId === selectedSite) ?? null;
  const nextPaymentDue = selectedSiteSummary?.AccountDetails?.NextPaymentDue
    ? Formatters.getFormattedDate(selectedSiteSummary.AccountDetails.NextPaymentDue)
    : null;
  const canAdministerSite =
    selectedSiteSummary?.UserType === UserType.SiteOwner ||
    selectedSiteSummary?.UserType === UserType.SiteAdmin;
  const canManageSubscribers =
    selectedSiteSummary?.UserType === UserType.SiteOwner ||
    selectedSiteSummary?.UserType === UserType.SiteAdmin;
  const canManageNewsletters =
    selectedSiteSummary?.UserType === UserType.SiteOwner ||
    selectedSiteSummary?.UserType === UserType.SiteAdmin ||
    selectedSiteSummary?.UserType === UserType.SiteWriter ||
    selectedSiteSummary?.UserType === UserType.SitePublisher;
  const access = cmsSettingsAccess(selectedSite);
  const canManageAccount = access.canManageBilling;
  const canEditSiteDetails = access.isSiteOwner;
  const canManageSiteUsers = canAdministerSite;
  const canManageSiteSettings = canAdministerSite;
  const isOnAccountDetailsPage = location.pathname.toLowerCase() === "/account/details";
  const paymentBannerMessage = isSuspended
    ? "This account is locked for changes until payment is made."
    : isOverdue
      ? "This account payment is overdue. Please make payment as soon as possible to avoid the account being locked."
      : `This account is due${nextPaymentDue ? ` on ${nextPaymentDue}` : ""}. Payment should be completed before the due date to avoid interruption.`;

  return (
    <>
      {(isApproachingPaymentDue || isOverdue || isSuspended) && (
        <Alert
          variant={isSuspended ? "danger" : "warning"}
          className="mb-0 rounded-0 border-start-0 border-end-0 border-top-0"
        >
          <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-2">
            <div>
              <strong>
                <FontAwesomeIcon icon={isSuspended ? faLock : faExclamationTriangle} className="me-2" />
                {isSuspended ? "Account locked" : "Payment due"}
              </strong>
              <div>{paymentBannerMessage}</div>
            </div>
            {canManageAccount && !isOnAccountDetailsPage && (
              <div>
                <Link to="/Account/Details" className="btn btn-outline-primary btn-sm">
                  Go to Account Details
                </Link>
              </div>
            )}
          </div>
        </Alert>
      )}
      <Navbar expand="lg" bg="light" className="shadow m-0 ps-2 pe-2 admin-shell__header" id="AdminMenu">
        <Navbar.Brand as={Link} to="/content" className="admin-brand" aria-label="SmoothBadger CMS home" title="SmoothBadger CMS home">
          <BrandMark />
          <span className="admin-brand__text"><strong>SmoothBadger CMS</strong><small>Website workspace</small></span>
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="mainNavContent" />
        <Navbar.Collapse id="mainNavContent">
          <Nav className="me-auto">
          {(!isSuspended || accType === AccountType.Complimentary) && (
            <>
              <NavDropdown title="Site Content" id="nav-site-content">
                <NavDropdown.Item as={Link} to="/content">Pages</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/content/menus">Menus</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/content/headers">Headers</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/Content/Footers">Footers</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/Content/PrivacyNotices">Privacy Policies</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/Content/ManageRedirects">Manage Redirects</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/Content/MediaManager">Media Manager</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/Content/StaticRoutes">Static Pages</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/Content/SocialMedia">Social Media Links</NavDropdown.Item>
              </NavDropdown>
              <NavDropdown title="SEO" id="nav-seo">
                <NavDropdown.Item disabled={!cacheRefresh.canRefresh} onClick={() => void cacheRefresh.refresh()} title={cacheRefresh.message}>
                  Refresh published pages
                </NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/Content/MetaData">Page Meta Data</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/Content/PageKeyword">Page Keywords</NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/Content/SiteMetaData">Site Meta Data</NavDropdown.Item>
              </NavDropdown>
              <NavDropdown title="Submitted Data" id="nav-submitted-data">
                <NavDropdown.Item as={Link} to="/Content/SubmittedData">Saved Data</NavDropdown.Item>
              </NavDropdown>
              {(canManageSubscribers || canManageNewsletters) && (
                <NavDropdown title="Subscribers" id="nav-subscribers">
                  {canManageSubscribers && (
                    <NavDropdown.Item as={Link} to="/Subscribers/Manage">Manage Subscribers</NavDropdown.Item>
                  )}
                  {canManageNewsletters && (
                    <NavDropdown.Item as={Link} to="/Subscribers/Newsletters">Newsletters</NavDropdown.Item>
                  )}
                </NavDropdown>
              )}
              {canManageSiteUsers && (
                <NavDropdown title="Users" id="nav-users">
                  <NavDropdown.Item as={Link} to="/Users/All">All Users</NavDropdown.Item>
                </NavDropdown>
              )}
            </>
          )}

          {(canManageSiteSettings || canManageAccount) && (
            <NavDropdown title="Settings" id="nav-settings">
              {canManageAccount && (
                <NavDropdown.Item as={Link} to="/Account/Details">Account Details</NavDropdown.Item>
              )}
              {canEditSiteDetails && (!isSuspended || accType === AccountType.Complimentary) && (
                <NavDropdown.Item as={Link} to="/Account/SiteDetails">Site Details</NavDropdown.Item>
              )}
              {canManageSiteSettings && (accType === AccountType.Complimentary || !isSuspended) && (
                <>
                  <NavDropdown.Item as={Link} to="/Content/Languages">Languages</NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/Content/ScriptAndCss">Scripts &amp; Stylesheets</NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/content/socialmedialinks">Social Media Links</NavDropdown.Item>
                </>
              )}
            </NavDropdown>
          )}
          {isAdmin && (
            <NavDropdown title="System Settings" id="nav-system-settings">
              <NavDropdown.Item as={Link} to="/SystemAdmin/Components">All Components</NavDropdown.Item>
              <NavDropdown.Item as={Link} to="/SystemAdmin/PropertyTypes">Property Types</NavDropdown.Item>
              <NavDropdown.Item as={Link} to="/SystemAdmin/LayoutTemplates">Templates</NavDropdown.Item>
            </NavDropdown>
          )}
        </Nav>
        <div className="admin-header__controls">
          <div className="admin-header__site">
          <FontAwesomeIcon icon={faBuilding} aria-hidden="true" />
          <FormSelect title="site selector" aria-label="Switch website"
            className="admin-header__site-select"
            value={selectedSite}
            onChange={handleSiteSelect}>
            {
              sites
                .sort((a, b) => a.SiteName.localeCompare(b.SiteName))
                .map((site: AvailableSite) => (
                  <option key={site.SiteId} value={site.SiteId}>{site.SiteName}</option>
                ))
            }
          </FormSelect>
          </div>
          <Dropdown align="end">
            <Dropdown.Toggle variant="link" className="nav-link hoverAvatar m-0 p-0 border-0 text-decoration-none admin-header__avatar-toggle">
              <FontAwesomeIcon icon={faUserShield} /><span className="admin-header__profile-name">{profileLabel}</span>
            </Dropdown.Toggle>
            <Dropdown.Menu className="list--avatar list--avatar--settings js-hoverAvatarList shadow">
              <YourAppsMenu current="cms" businessId={selectedSite} />
              <Dropdown.Item as={Link} to="/users/mydetails">
                <FontAwesomeIcon icon={faIdCard} />&nbsp;My Details
              </Dropdown.Item>
              <Dropdown.Item as={Link} to="/Users/ChangePassword">
                <FontAwesomeIcon icon={faUnlockKeyhole} />&nbsp;Change Password
              </Dropdown.Item>
              <div className="list--avatar__section">
                <label className="list--avatar__label" htmlFor="ThemeSelectorMenu">Theme</label>
                <FormSelect
                  id="ThemeSelectorMenu"
                  name="ThemeSelectorMenu"
                  title="Theme selector"
                  className="list--avatar__theme-select"
                  value={theme}
                  onChange={(evt) => setTheme(evt.target.value as AppTheme)}
                >
                  {availableThemes.map((thm) => (
                    <option key={`THEME-M-${thm}`} value={thm}>
                      Theme: {themeLabels[thm]}
                    </option>
                  ))}
                </FormSelect>
              </div>
              <Dropdown.Divider />
              {userHasSiteAccount && (
                <>
                  <Dropdown.Item as={Link} to="/Users/RegisterNewSite">
                    <FontAwesomeIcon icon={faGlobe} />&nbsp;Register a New Site
                  </Dropdown.Item>
                  <Dropdown.Divider />
                </>
              )}
            </Dropdown.Menu>
          </Dropdown>
          <SignOutButton appName="CMS" variant="outline-secondary" className="admin-header__signout" onConfirm={handleSignOut}>
            <FontAwesomeIcon icon={faArrowRightFromBracket} /> Sign out
          </SignOutButton>
        </div>
        </Navbar.Collapse>
      </Navbar>
    </>
  )
};

export default AdminHeader;
